import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/lib/api'
import type { Campaign, Referral } from '@/lib/referral-types'
import { formatCents, formatTs, referralStatusVariant } from '@/lib/referral-utils'
import { Card } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Badge } from '@/components/ui/Badge'
import { PiiField } from '@/components/ui/PiiField'
import { Modal } from '@/components/ui/Modal'
import { usePresentation } from '@/components/layout/AppShell'

export function ReferralListPage() {
  const { presenting } = usePresentation()
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState('')
  const [campaignFilter, setCampaignFilter] = useState('')
  const [selected, setSelected] = useState<Referral | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionResult, setActionResult] = useState<{ success: boolean; message: string } | null>(null)
  const [clawbackReason, setClawbackReason] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['admin-referrals', { status: statusFilter, campaign_id: campaignFilter }],
    queryFn: () =>
      authApi.get<{ referrals: Referral[] }>('/api/v1/admin/referral/referrals', {
        params: {
          status: statusFilter || undefined,
          campaign_id: campaignFilter || undefined,
          limit: 100,
        },
      }),
  })

  const { data: campaignsData } = useQuery({
    queryKey: ['admin-referral-campaigns'],
    queryFn: () => authApi.get<{ campaigns: Campaign[] }>('/api/v1/admin/referral/campaigns'),
  })

  async function handleApproveReward(rewardId: string) {
    setActionLoading(true)
    setActionResult(null)
    try {
      await authApi.post(`/api/v1/admin/referral/rewards/${rewardId}/approve`)
      setActionResult({ success: true, message: 'Reward approved and credited.' })
      queryClient.invalidateQueries({ queryKey: ['admin-referrals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-referral-overview'] })
    } catch (e) {
      setActionResult({ success: false, message: String(e) })
    } finally {
      setActionLoading(false)
    }
  }

  async function handleClawbackReward(rewardId: string) {
    setActionLoading(true)
    setActionResult(null)
    try {
      await authApi.post(`/api/v1/admin/referral/rewards/${rewardId}/clawback`, {
        reason: clawbackReason || undefined,
      })
      setActionResult({ success: true, message: 'Reward clawed back.' })
      setClawbackReason('')
      queryClient.invalidateQueries({ queryKey: ['admin-referrals'] })
      queryClient.invalidateQueries({ queryKey: ['admin-referral-overview'] })
    } catch (e) {
      setActionResult({ success: false, message: String(e) })
    } finally {
      setActionLoading(false)
    }
  }

  const columns: Column<Referral>[] = [
    {
      id: 'referrer',
      header: 'Referrer',
      cell: (row) => (
        <div>
          <PiiField value={row.referrer_name} presenting={presenting} className="font-medium text-sm" />
          <PiiField value={row.referrer_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
        </div>
      ),
    },
    {
      id: 'referred',
      header: 'Referred',
      cell: (row) => (
        <div>
          <PiiField value={row.referred_name} presenting={presenting} className="font-medium text-sm" />
          <PiiField value={row.referred_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
        </div>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      cell: (row) => <Badge variant={referralStatusVariant(row.status)}>{row.status}</Badge>,
    },
    {
      id: 'fraud',
      header: 'Fraud',
      cell: (row) =>
        row.fraud_score > 0 ? (
          <Badge variant="error">{row.fraud_score} flag{row.fraud_score > 1 ? 's' : ''}</Badge>
        ) : (
          <span className="text-[var(--content-tertiary)]">--</span>
        ),
    },
    {
      id: 'attribution_type',
      header: 'Attribution',
      cell: (row) => <span className="capitalize text-[var(--content-secondary)]">{row.attribution_type}</span>,
    },
    {
      id: 'referred_mrr',
      header: 'MRR',
      cell: (row) =>
        row.referred_mrr ? (
          <span className="tabular-nums">{formatCents(row.referred_mrr)}</span>
        ) : (
          <span className="text-[var(--content-tertiary)]">--</span>
        ),
      className: 'text-right',
    },
    {
      id: 'created_at',
      header: 'Referred At',
      cell: (row) => <span className="text-[var(--content-secondary)] tabular-nums">{formatTs(row.created_at)}</span>,
    },
    {
      id: 'converted_at',
      header: 'Converted',
      cell: (row) => <span className="text-[var(--content-secondary)] tabular-nums">{formatTs(row.converted_at)}</span>,
    },
  ]

  return (
    <div>
      <h1 className="text-2xl font-heading font-semibold mb-6">Referrals</h1>

      {/* Filters */}
      <Card className="mb-4">
        <div className="px-4 py-3 flex items-center gap-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="trial">Trial</option>
            <option value="converted">Converted</option>
            <option value="churned">Churned</option>
            <option value="fraudulent">Fraudulent</option>
          </select>
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
          >
            <option value="">All Campaigns</option>
            {campaignsData?.campaigns?.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {data && (
            <span className="ml-auto text-sm text-[var(--content-tertiary)]">
              {data.referrals.length} result{data.referrals.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </Card>

      <Card>
        <DataTable
          columns={columns}
          data={data?.referrals ?? []}
          keyExtractor={(row) => row.id}
          onRowClick={(row) => { setSelected(row); setActionResult(null) }}
          loading={isLoading}
          emptyMessage="No referrals found"
        />
      </Card>

      {/* Referral detail modal */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title="Referral Details"
      >
        {selected && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-[var(--content-tertiary)]">Referrer</p>
                <PiiField value={selected.referrer_name} presenting={presenting} className="font-medium" />
                <PiiField value={selected.referrer_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
              </div>
              <div>
                <p className="text-xs text-[var(--content-tertiary)]">Referred</p>
                <PiiField value={selected.referred_name} presenting={presenting} className="font-medium" />
                <PiiField value={selected.referred_email} presenting={presenting} className="text-xs text-[var(--content-tertiary)]" />
              </div>
              <div>
                <p className="text-xs text-[var(--content-tertiary)]">Status</p>
                <Badge variant={referralStatusVariant(selected.status)}>{selected.status}</Badge>
              </div>
              <div>
                <p className="text-xs text-[var(--content-tertiary)]">Attribution</p>
                <p className="capitalize">{selected.attribution_type}</p>
              </div>
              <div>
                <p className="text-xs text-[var(--content-tertiary)]">MRR</p>
                <p className="tabular-nums">{formatCents(selected.referred_mrr)}</p>
              </div>
              <div>
                <p className="text-xs text-[var(--content-tertiary)]">Referred At</p>
                <p className="tabular-nums">{formatTs(selected.created_at)}</p>
              </div>
              {selected.fraud_score > 0 && (
                <div className="col-span-2">
                  <p className="text-xs text-[var(--content-tertiary)]">Fraud Flags</p>
                  <p className="text-sm text-red-600 dark:text-red-400">
                    Score: {selected.fraud_score} — {selected.fraud_flags || 'Unknown flags'}
                  </p>
                </div>
              )}
            </div>

            {/* Reward actions placeholder — rewards need to be fetched from referral detail */}
            <div className="border-t border-[var(--border)] pt-3">
              <p className="text-xs font-semibold text-[var(--content-tertiary)] uppercase tracking-wider mb-2">
                Quick Actions
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleApproveReward(selected.id)}
                  disabled={actionLoading}
                  className="px-3 py-1.5 text-xs rounded-full bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  {actionLoading ? 'Processing...' : 'Approve Pending Rewards'}
                </button>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Clawback reason..."
                    value={clawbackReason}
                    onChange={(e) => setClawbackReason(e.target.value)}
                    className="px-3 py-1.5 text-xs bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none"
                  />
                  <button
                    onClick={() => handleClawbackReward(selected.id)}
                    disabled={actionLoading}
                    className="px-3 py-1.5 text-xs rounded-full bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors cursor-pointer"
                  >
                    Clawback
                  </button>
                </div>
              </div>
            </div>

            {actionResult && (
              <p className={`text-xs ${actionResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                {actionResult.message}
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}
